// src/components/Stats/IpPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const API = process.env.REACT_APP_API_URL;

// -----------------------------------------------------------------------------
// Composant principal
// -----------------------------------------------------------------------------
export default function IpPanel() {
  // Filtres haut
  const [servers, setServers] = useState([]); // ['shark04', 'shark05', ...]
  const [serverIps, setServerIps] = useState({}); // { shark04: ['84.234...','...'], ... }
  const [server, setServer] = useState('');
  const [ip, setIp] = useState('');
  const [from, setFrom] = useState(() => isoDateNDaysAgo(14));
  const [to, setTo] = useState(() => isoDateNDaysAgo(0));
  const [activeSender, setActiveSender] = useState(''); // filtre sender (vide = global)

  // Données & états
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [error, setError] = useState('');

  // Table senders (filtre)
  const [tableSearch, setTableSearch] = useState('');

  // ---------------------------------------------------------------------------
  // Charger les serveurs et IPs (dépendance : API /stats/servers/options)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let run = true;
    (async () => {
      setLoadingMeta(true);
      try {
        // Essai 1 : endpoint serveur → {rows:[{server, ip}]}
        const res = await fetch(`${API}/stats/servers/options`);
        if (res.ok) {
          const data = await res.json();
          const rows = Array.isArray(data?.rows) ? data.rows : [];
          const map = new Map();
          for (const r of rows) {
            const s = String(r.server || '(inconnu)');
            const ip = String(r.ip || '').trim();
            if (!ip) continue;
            if (!map.has(s)) map.set(s, new Set());
            map.get(s).add(ip);
          }
          if (map.size === 0) throw new Error('no-rows');
          const obj = {};
          Array.from(map.entries()).forEach(
            ([k, set]) => (obj[k] = Array.from(set))
          );
          if (run) {
            setServers(Object.keys(obj).sort());
            setServerIps(obj);
            // sélection par défaut
            const firstServer = Object.keys(obj).sort()[0] || '';
            setServer(firstServer);
            setIp(obj[firstServer]?.[0] || '');
          }
        } else {
          // Fallback : /stats/ips/options → rows: ['ip1','ip2',...]
          const r2 = await fetch(`${API}/stats/ips/options`);
          const d2 = await r2.json();
          const ips = Array.isArray(d2?.rows) ? d2.rows : [];
          const obj = { '(inconnu)': ips };
          if (run) {
            setServers(Object.keys(obj));
            setServerIps(obj);
            setServer('(inconnu)');
            setIp(ips[0] || '');
          }
        }
      } catch {
        // Encore fallback dur : aucun endpoint dispo
        if (run) {
          setServers([]);
          setServerIps({});
          setServer('');
          setIp('');
        }
      } finally {
        setLoadingMeta(false);
      }
    })();
    return () => {
      run = false;
    };
  }, []);

  // Quand serveur change → réinitialiser IP (1ère IP du serveur) et filtre sender
  useEffect(() => {
    if (!server) return;
    const ips = serverIps[server] || [];
    const nextIp = ips[0] || '';
    setIp(nextIp);
    setActiveSender('');
  }, [server, serverIps]);

  // Quand IP change → revenir en vue globale (sans sender) puis charger
  useEffect(() => {
    if (!ip) {
      setSummary(null);
      return;
    }
    setActiveSender(''); // global par défaut
  }, [ip]);

  // ---------------------------------------------------------------------------
  // Charger la synthèse IP (+sender facultatif)
  // ---------------------------------------------------------------------------
  const abortRef = useRef(null);
  const fetchSummary = async () => {
    if (!ip) {
      setSummary(null);
      return;
    }
    setLoading(true);
    setError('');
    abortRef.current?.abort?.();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const params = new URLSearchParams({ ip, from, to });
      if (activeSender) params.set('sender', activeSender);
      const url = `${API}/stats/ip/summary?${params.toString()}`;
      const res = await fetch(url, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSummary(data);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ip, from, to, activeSender]);

  // ---------------------------------------------------------------------------
  // Préparations UI
  // ---------------------------------------------------------------------------
  const bounceTotalPeriod = useMemo(() => {
    if (!summary) return 0;
    return (
      (summary.totals_period?.bounces?.hard || 0) +
      (summary.totals_period?.bounces?.soft || 0)
    );
  }, [summary]);

  const bounceRate = useMemo(() => {
    if (!summary) return '-';
    const sent = summary.totals_period?.sent || 0;
    const b = bounceTotalPeriod;
    return sent ? `${Math.round((b / sent) * 100)}%` : '-';
  }, [summary, bounceTotalPeriod]);

  // Dataset couleurs (empilé)
  const chartData = useMemo(() => {
    if (!summary?.daily) return null;
    const hardNonBlocked = summary.daily.hard.map((h, i) =>
      Math.max(0, (h || 0) - (summary.daily.blocked[i] || 0))
    );
    return {
      labels: summary.daily.days,
      datasets: [
        {
          label: 'Envoyés',
          data: summary.daily.sent,
          stack: 's',
          backgroundColor: 'rgba(37, 99, 235, 0.25)', // bleu 600 transparent
          borderColor: 'rgba(37, 99, 235, 1)',
          borderWidth: 1,
        },
        {
          label: 'Soft (4xx)',
          data: summary.daily.soft,
          stack: 'b',
          backgroundColor: 'rgba(245, 158, 11, 0.3)', // amber 500
          borderColor: 'rgba(245, 158, 11, 1)',
          borderWidth: 1,
        },
        {
          label: 'Hard (5xx) non-blocked',
          data: hardNonBlocked,
          stack: 'b',
          backgroundColor: 'rgba(239, 68, 68, 0.25)', // red 500
          borderColor: 'rgba(239, 68, 68, 1)',
          borderWidth: 1,
        },
        {
          label: 'Blocked',
          data: summary.daily.blocked,
          stack: 'b',
          backgroundColor: 'rgba(185, 28, 28, 0.45)', // red 700 un peu plus sombre
          borderColor: 'rgba(185, 28, 28, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [summary]);

  const chartOpts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top' },
        tooltip: { mode: 'index', intersect: false },
      },
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
      },
    }),
    []
  );

  const providerDomains = useMemo(() => {
    const list =
      summary?.meta?.providers?.map(p => p.domain).filter(Boolean) || [];
    return Array.from(new Set(list)).sort();
  }, [summary]);

  const sendersRows = useMemo(() => {
    let rows = summary?.senders || [];
    if (tableSearch) {
      const t = tableSearch.toLowerCase();
      rows = rows.filter(
        r =>
          String(r.sender || '')
            .toLowerCase()
            .includes(t) ||
          String(r.provider_name || '')
            .toLowerCase()
            .includes(t)
      );
    }
    return rows.map(r => ({
      ...r,
      bounce_rate_period: r.sent_period
        ? `${Math.round(((r.hard_period + r.soft_period) / r.sent_period) * 100)}%`
        : '-',
    }));
  }, [summary, tableSearch]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6 mt-16">
      {/* Barre de filtres */}
      <div className="bg-white rounded-2xl border p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          {/* Serveur */}
          <div className="min-w-[220px]">
            <label className="block text-xs mb-1">Serveur</label>
            <select
              className="border rounded px-2 py-1 w-[220px]"
              value={server}
              onChange={e => setServer(e.target.value)}
              disabled={loadingMeta}
            >
              {servers.length === 0 && <option value="">— aucun —</option>}
              {servers.map(s => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* IP sortante (dépend du serveur) */}
          <div className="min-w-[260px]">
            <label className="block text-xs mb-1">IP sortante</label>
            <select
              className="border rounded px-2 py-1 w-[260px]"
              value={ip}
              onChange={e => setIp(e.target.value)}
              disabled={!server || (serverIps[server]?.length || 0) === 0}
            >
              {(serverIps[server] || []).map(v => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div>
            <label className="block text-xs mb-1">Du</label>
            <input
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
              className="border rounded px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-xs mb-1">Au</label>
            <input
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
              className="border rounded px-2 py-1"
            />
          </div>
          <QuickRange
            onPick={d => {
              setFrom(isoDateNDaysAgo(d));
              setTo(isoDateNDaysAgo(0));
            }}
          />

          {/* Sender select (facultatif, après charge de summary) */}
          <div className="min-w-[280px]">
            <label className="block text-xs mb-1">Sender</label>
            <select
              className="border rounded px-2 py-1 w-[280px]"
              value={activeSender}
              onChange={e => setActiveSender(e.target.value)}
              disabled={!providerDomains.length}
            >
              <option value="">— Tous (global IP) —</option>
              {providerDomains.map(d => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <button
            className="ml-auto px-3 py-1 rounded bg-blue-600 text-white text-sm disabled:opacity-60"
            onClick={fetchSummary}
            disabled={loading || !ip}
            title="Rafraîchir maintenant"
          >
            {loading ? 'Chargement…' : 'Rafraîchir'}
          </button>
        </div>

        {/* Chips Providers cliquables */}
        {summary?.meta && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-500">Serveur(s):</span>
            {summary.meta.servers?.length ? (
              summary.meta.servers.map(s => (
                <span
                  key={s}
                  className="px-2 py-0.5 rounded-full border bg-gray-50"
                >
                  {s}
                </span>
              ))
            ) : (
              <span className="text-gray-400 italic">inconnu</span>
            )}
            <span className="mx-2 text-gray-300">•</span>
            <span className="text-gray-500">Providers:</span>
            {summary.meta.providers?.length ? (
              summary.meta.providers.map(p => {
                const domain = p.domain || '';
                const active =
                  activeSender &&
                  activeSender.toLowerCase() === domain.toLowerCase();
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setActiveSender(active ? '' : domain)}
                    className={`px-2 py-0.5 rounded-full border ${active ? 'bg-blue-600 text-white' : 'bg-gray-50 hover:bg-gray-100'}`}
                    title={domain}
                  >
                    {p.name || domain}
                  </button>
                );
              })
            ) : (
              <span className="text-gray-400 italic">n/a</span>
            )}
            {activeSender && (
              <>
                <span className="mx-2 text-gray-300">•</span>
                <button
                  className="text-blue-600 underline decoration-dotted"
                  onClick={() => setActiveSender('')}
                >
                  Voir global IP
                </button>
              </>
            )}
          </div>
        )}

        {error && <div className="text-sm text-red-600">Erreur: {error}</div>}
        {!ip && !loadingMeta && (
          <div className="text-sm text-gray-500">
            Choisissez d’abord un serveur puis une IP.
          </div>
        )}
      </div>

      {/* KPIs */}
      {summary ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <KPI
              title={`Envoyés ${activeSender ? '(filtré)' : '(période)'}`}
              value={summary.totals_period?.sent}
              loading={loading}
            />
            <KPI
              title="Bounces (période)"
              value={
                (summary.totals_period?.bounces?.hard || 0) +
                (summary.totals_period?.bounces?.soft || 0)
              }
              loading={loading}
            />
            <KPI
              title="Hard (5xx)"
              value={summary.totals_period?.bounces?.hard}
              loading={loading}
            />
            <KPI
              title="Soft (4xx)"
              value={summary.totals_period?.bounces?.soft}
              loading={loading}
            />
            <KPI
              title="Blocked (période)"
              value={summary.totals_period?.bounces?.blocked}
              loading={loading}
            />
            <KPI title="Bounce rate" value={bounceRate} loading={loading} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI
              title={`Envoyés ${activeSender ? '(filtré)' : '(ALL)'}`}
              value={summary.totals_all?.sent}
              loading={loading}
            />
            <KPI
              title="Bounces (ALL)"
              value={summary.totals_all?.bounces?.total}
              loading={loading}
            />
            <KPI
              title="Hard (ALL)"
              value={summary.totals_all?.bounces?.hard}
              loading={loading}
            />
            <KPI
              title="Soft (ALL)"
              value={summary.totals_all?.bounces?.soft}
              loading={loading}
            />
          </div>
        </>
      ) : (
        <EmptyCard
          text={loadingMeta ? 'Chargement des métadonnées…' : 'Aucune donnée'}
        />
      )}

      {/* Graph + Top bloqueurs */}
      {summary && (
        <div className="grid grid-cols-1 2xl:grid-cols-3 gap-6">
          <Card
            className="2xl:col-span-2"
            title={`Séries journalières · ${summary.ip}${activeSender ? ` · ${activeSender}` : ''}`}
          >
            <div className="relative h-80">
              {loading && <OverlayLoader />}
              <Bar data={chartData} options={chartOpts} />
            </div>
          </Card>

          <Card title="Top bloqueurs (période)">
            <SimpleTable
              rows={(summary.topBlockers || []).map(r => ({
                domain: r.domain,
                count: r.count,
                sample_code: r.sample_code,
              }))}
              columns={[
                { key: 'domain', label: 'Domaine' },
                { key: 'count', label: 'Bloqués' },
                { key: 'sample_code', label: 'DSN' },
              ]}
            />
          </Card>
        </div>
      )}

      {/* Senders sur cette IP */}
      {summary && (
        <Card title="Senders sur cette IP · métriques période & all-time">
          <div className="mb-2 flex items-center gap-2">
            <input
              value={tableSearch}
              onChange={e => setTableSearch(e.target.value)}
              placeholder="Filtrer par sender/provider…"
              className="border rounded px-2 py-1 text-sm w-64"
            />
            <span className="text-xs text-gray-500">
              {(summary.senders || []).length} lignes
            </span>
          </div>
          <div className="overflow-auto rounded-xl border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {[
                    { k: 'sender', l: 'Sender' },
                    { k: 'provider_name', l: 'Provider' },
                    { k: 'server', l: 'Server' },
                    { k: 'sent_period', l: 'Envoyés (P)' },
                    { k: 'hard_period', l: 'Hard (P)' },
                    { k: 'soft_period', l: 'Soft (P)' },
                    { k: 'blocked_period', l: 'Blocked (P)' },
                    { k: 'bounce_rate_period', l: 'Bounce rate (P)' },
                    { k: 'sent_all', l: 'Envoyés (ALL)' },
                    { k: 'hard_all', l: 'Hard (ALL)' },
                    { k: 'soft_all', l: 'Soft (ALL)' },
                    { k: 'blocked_all', l: 'Blocked (ALL)' },
                  ].map(c => (
                    <th
                      key={c.k}
                      className="text-left px-3 py-2 border-b whitespace-nowrap"
                    >
                      {c.l}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sendersRows.length === 0 && (
                  <tr>
                    <td className="px-3 py-2 text-gray-400 italic" colSpan={12}>
                      Aucune donnée
                    </td>
                  </tr>
                )}
                {sendersRows.map((r, i) => {
                  const isActive =
                    activeSender &&
                    activeSender.toLowerCase() ===
                      String(r.sender || '').toLowerCase();
                  return (
                    <tr
                      key={i}
                      className={`odd:bg-white even:bg-gray-50 ${isActive ? 'bg-blue-50' : 'hover:bg-blue-50 cursor-pointer'}`}
                      onClick={() => setActiveSender(isActive ? '' : r.sender)}
                      title="Voir les stats de ce sender"
                    >
                      <td className="px-3 py-2 border-b">{r.sender}</td>
                      <td className="px-3 py-2 border-b">
                        {r.provider_name || '—'}
                      </td>
                      <td className="px-3 py-2 border-b">{r.server || '—'}</td>
                      <td className="px-3 py-2 border-b">
                        {formatNumber(r.sent_period)}
                      </td>
                      <td className="px-3 py-2 border-b">
                        {formatNumber(r.hard_period)}
                      </td>
                      <td className="px-3 py-2 border-b">
                        {formatNumber(r.soft_period)}
                      </td>
                      <td className="px-3 py-2 border-b">
                        {formatNumber(r.blocked_period)}
                      </td>
                      <td className="px-3 py-2 border-b">
                        {r.bounce_rate_period}
                      </td>
                      <td className="px-3 py-2 border-b">
                        {formatNumber(r.sent_all)}
                      </td>
                      <td className="px-3 py-2 border-b">
                        {formatNumber(r.hard_all)}
                      </td>
                      <td className="px-3 py-2 border-b">
                        {formatNumber(r.soft_all)}
                      </td>
                      <td className="px-3 py-2 border-b">
                        {formatNumber(r.blocked_all)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// UI helpers
// -----------------------------------------------------------------------------
function QuickRange({ onPick }) {
  return (
    <div className="flex gap-1">
      {[7, 14, 30].map(n => (
        <button
          key={n}
          className="px-2 py-1 border rounded text-xs"
          onClick={() => onPick(n)}
        >
          {n}j
        </button>
      ))}
    </div>
  );
}

function Card({ title, className = '', children }) {
  return (
    <div className={`bg-white rounded-2xl border shadow-sm p-4 ${className}`}>
      <div className="text-sm font-semibold text-gray-700 mb-2">{title}</div>
      {children}
    </div>
  );
}

function EmptyCard({ text }) {
  return (
    <div className="rounded-2xl border shadow-sm p-8 text-center text-gray-500 bg-white">
      {text}
    </div>
  );
}

function KPI({ title, value, loading }) {
  return (
    <div className="rounded-2xl border shadow-sm p-4 bg-white">
      <div className="text-xs text-gray-500 mb-1">{title}</div>
      {loading ? (
        <div className="h-7 w-24 bg-gray-200 rounded animate-pulse" />
      ) : (
        <div className="text-2xl font-bold">{formatNumber(value)}</div>
      )}
    </div>
  );
}

function SimpleTable({ rows, columns }) {
  return (
    <div className="overflow-auto rounded-xl border">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-50">
            {columns.map(c => (
              <th
                key={c.key}
                className="text-left px-3 py-2 border-b whitespace-nowrap"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(!rows || rows.length === 0) && (
            <tr>
              <td
                className="px-3 py-2 text-gray-400 italic"
                colSpan={columns.length}
              >
                Aucune donnée
              </td>
            </tr>
          )}
          {rows?.map((r, i) => (
            <tr key={i} className="odd:bg-white even:bg-gray-50">
              {columns.map(c => (
                <td key={c.key} className="px-3 py-2 border-b align-top">
                  {String(r[c.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Loader overlay pour le graphe
function OverlayLoader() {
  return (
    <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
      <Spinner />
    </div>
  );
}
function Spinner() {
  return (
    <div className="h-6 w-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
  );
}

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------
function isoDateNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function formatNumber(n) {
  if (typeof n === 'string' && /^\d+%$/.test(n)) return n;
  return new Intl.NumberFormat('fr-FR').format(Number(n || 0));
}
